import React, { useRef, useCallback, useState, useMemo } from 'react'
import staticStore from '@aytm/app/insights/dashboard/stores/static.store'
import ADND from '@aytm/lib/adnd/ADND'
import DragBar from './DragBar'
import { decode } from '@aytm/app/insights/dashboard/lib/sanitize'
import { OverlayPanel } from 'primereact/overlaypanel'
import { Tooltip } from 'primereact/tooltip'
import { ListBox, type ListBoxProps } from 'primereact/listbox'
import { AttributeOptionsList } from './AttributeOptionsList'
import { prettyMetricsName } from './helpers'
import { useAttributes, useDashboard, useExclusiveOverlay } from '@aytm/app/insights/dashboard/components/hooks'
import { useSections } from '../SectionToggle/SectionToggle.hooks'
import type { CaptionType } from '@aytm/app/insights/dashboard/models/types.ClientDTO'
import type { SortOrder } from '@aytm/app/insights/dashboard/models/BlockModel'
import type { Identity } from '@aytm/app/insights/dashboard/helperTypes'
import classNames from 'classnames'
import mcss from './Attribute.module.scss'
import './Attribute.scss'

const menuOptions = [
  {
    label: (
      <span title="Apply the metrics of the current attribute to the rest of the attributes in this section">
        Apply metrics to this section
      </span>
    ),
    value: 'apply'
  }
]

const CLASS = 'insights-portal-attribute'

const emptyArray = [] as string[]

type AttributeProps = {
  /**
   * event - { <Attribute name>: { values: [ <method id>, ... ], method: <method id>, show: <true/false> } }
   * values - Metrics method ids to show
   * method - Default attribute method
   * show - Show/Hide metrics for this attribute
   */
  caption: CaptionType
  className?: string
  currentSplit?: string
  drag?: boolean
  // enableChart?: boolean
  enableSort?: boolean
  onClick?: (caption: Partial<CaptionType>) => void
  onDelete?: (attribute: Partial<CaptionType>) => void
  onDrag?: (
    arg0: {
      action: 'drop' | 'enter' | 'leave'
      data: Partial<CaptionType> & {
        [key: string]: unknown
        newSection?: string
      }
    },
    arg1?: unknown
  ) => void
  onMetricsChange?: {
    (item: unknown): void
    (item: unknown, metric: string): void
  }
  onReplace?: (arg: { from: CaptionType; to: Partial<CaptionType> }) => void
  sortByAttribute?: string
  sortByMethod?: string
  chartAttribute?: string
  chartMethod?: string | null
  sortOrder?: SortOrder
  subjectType?: string
}

export const Attribute: React.FC<AttributeProps> & { Placeholder: React.FC } = ({
  className: propsClassName,
  caption,
  drag: propsDrag = false,
  sortOrder: propsSortOrder = 'asc',
  sortByAttribute: propsSortByAttribute,
  sortByMethod: propsSortByMethod,
  chartAttribute: propsChartAttribute,
  chartMethod: propsChartMethod,
  currentSplit: propsCurrentSplit,
  enableSort: propsEnableSort = true,
  onDelete: propsOnDelete,
  onClick: propsOnClick,
  onReplace: propsOnReplace,
  onDrag: propsOnDrag = null,
  onMetricsChange: propsOnMetricsChange
}) => {
  const className = propsClassName || CLASS

  const { isSharedView, featureMultiMetrics } = staticStore

  const {
    type,
    metricsOptions,
    defaultMetrics,
    metrics,
    selectedMetrics = emptyArray,
    section,
    attribute,
    target,
    showMetrics = false
  } = caption

  // const selectedMetrics = caption.selectedMetrics || emptyArray
  const metricsMethodId = metrics?.id || null
  const defaultMetricsMethodId = defaultMetrics?.id || null

  const { editorMode } = useDashboard()
  const { getCurrentSectionItems, onApplyMetricsToSection } = useSections()
  const { attributes } = useAttributes()

  const currentSectionItems = useMemo(
    () => getCurrentSectionItems(section, target),
    [getCurrentSectionItems, section, target]
  )

  const isSortTarget = useMemo(
    () => Boolean(propsSortByAttribute === attribute && propsSortByMethod === metricsMethodId),
    [metricsMethodId, attribute, propsSortByAttribute, propsSortByMethod]
  )

  // Guard (propsSortByAttribute !== propsChartAttribute) is intentional: when sorting by a regular
  // attribute, sortByAttribute === chartAttribute, so without it every non-active metric sub-row
  // of that attribute would also go bold in featureMultiMetrics mode.
  const selected = useMemo(
    () =>
      Boolean(
        isSortTarget ||
        (propsChartAttribute === attribute &&
          propsSortByAttribute !== propsChartAttribute &&
          propsChartMethod === metricsMethodId)
      ),
    [isSortTarget, propsChartAttribute, attribute, propsSortByAttribute, propsChartMethod, metricsMethodId]
  )

  const sorted = useMemo(
    () =>
      Boolean(isSortTarget && propsEnableSort && propsSortOrder && 'manual' !== propsSortOrder && !propsCurrentSplit),
    [propsCurrentSplit, propsEnableSort, propsSortOrder, isSortTarget]
  )

  const thisDOMElRef = useRef<HTMLDivElement>(null)

  const onDragRef = useRef<AttributeProps['onDrag']>()
  onDragRef.current = propsOnDrag

  const onReplaceRef = useRef<AttributeProps['onReplace']>()
  onReplaceRef.current = propsOnReplace

  const dragSource = useRef<Partial<Identity<ReturnType<typeof ADND.targets.data>>>>()
  if (null == dragSource.current) {
    dragSource.current = ADND.targets
      .data({
        acceptTypes: ['attribute']
      })
      .onEnter(api => {
        const data: CaptionType = api.getData()
        if (data?.attribute == attribute && data?.section == section && data?.target == target) {
          return
        }
        if (onDragRef.current) {
          onDragRef.current({ action: 'enter', data: { attribute, section, target } as Partial<CaptionType> })
        }
      })
      .onLeave(() => {
        if (onDragRef.current) {
          onDragRef.current({ action: 'leave', data: { attribute, section, target } as Partial<CaptionType> })
        }
      })
      .onDrop(event => {
        const eventData = event.data as CaptionType
        if (eventData.attribute && onReplaceRef.current) {
          onReplaceRef.current({
            from: eventData,
            to: { attribute, section, target } as Partial<CaptionType>
          })
        } else if (eventData.attribute && onDragRef.current) {
          onDragRef.current({
            action: 'drop',
            data: { attribute, section, target }
          })
        }
      })
  }

  const [showPanelContent, setShowPanelContent] = useState<string | null>(null)

  const panelRef = useRef<OverlayPanel>()
  const exclusiveOverlay = useExclusiveOverlay()

  const [isOptionsOpen, setIsOptionsOpen] = useState(false)
  const toggleOptions = useCallback(
    (event: React.MouseEvent<HTMLElement> | Parameters<ListBoxProps['onChange']>[0]) => {
      event.preventDefault()
      event.stopPropagation()
      if (!metricsOptions?.length) {
        return
      }
      setIsOptionsOpen(state => !state)
      if (isOptionsOpen) {
        exclusiveOverlay(panelRef.current, panel => panel?.hide())
        setShowPanelContent(null)
      } else {
        setShowPanelContent(state => state ?? 'metrics')
        exclusiveOverlay(panelRef.current, panel => panel?.show?.(null, thisDOMElRef.current))
      }
    },
    [exclusiveOverlay, isOptionsOpen, metricsOptions?.length]
  )

  const toggleExtraMenu: React.MouseEventHandler<HTMLElement> = event => {
    setShowPanelContent('menu')
    return toggleOptions(event)
  }

  const handleAttributeMenu = useCallback(
    (event: Parameters<ListBoxProps['onChange']>[0]) => {
      event.preventDefault()
      event.stopPropagation()
      switch (event.value) {
        case 'apply': {
          onApplyMetricsToSection(section, target, metrics?.id ?? defaultMetrics?.id)
          break
        }
        default: {
          break
        }
      }
      return toggleOptions(event)
    },
    [toggleOptions, metrics, defaultMetrics, onApplyMetricsToSection, section, target]
  )

  const handleSelectMetrics = useCallback(
    (metricsId: string) => {
      exclusiveOverlay(panelRef.current, panel => panel?.hide())

      attribute &&
        propsOnMetricsChange?.({
          [attribute]: {
            section,
            target,
            values: selectedMetrics.includes(metricsId) ? selectedMetrics : [...selectedMetrics, metricsId],
            show: true
          }
        })
    },
    [exclusiveOverlay, attribute, propsOnMetricsChange, section, target, selectedMetrics]
  )

  /**
   * Invoked to show/hide sub-metrics
   */
  const handleToggleMetrics = useCallback<React.MouseEventHandler<HTMLElement>>(
    evt => {
      evt.preventDefault()
      evt.stopPropagation()
      propsOnMetricsChange?.({ [attribute]: { section, target, show: !showMetrics } })
    },
    [attribute, propsOnMetricsChange, section, target, showMetrics]
  )

  const handleTextClick = useCallback(() => {
    if (propsOnClick) {
      propsOnClick({
        type,
        attribute,
        section,
        target,
        method: metricsMethodId,
        defaultMethod: defaultMetricsMethodId
      } as Partial<CaptionType>)
    }
  }, [attribute, defaultMetricsMethodId, metricsMethodId, propsOnClick, section, target, type])

  const handleDeleteClick = useCallback(() => {
    if (propsOnDelete) {
      propsOnDelete({
        type,
        attribute,
        section,
        target,
        method: metricsMethodId,
        defaultMethod: defaultMetricsMethodId
      } as Partial<CaptionType>)
    }
  }, [attribute, defaultMetricsMethodId, metricsMethodId, propsOnDelete, section, target, type])

  const handleStarClick = useCallback<React.MouseEventHandler<HTMLElement>>(
    event => {
      event.preventDefault()
      event.stopPropagation()
      const defaultMetricsId = defaultMetrics?.id
      attribute &&
        propsOnMetricsChange?.({
          [attribute]: {
            section,
            target,
            method: metricsMethodId,
            ...(defaultMetricsId
              ? { values: [defaultMetricsId, ...selectedMetrics].filter(methodId => methodId !== metricsMethodId) }
              : {})
          }
        })
    },
    [attribute, defaultMetrics?.id, metricsMethodId, propsOnMetricsChange, section, target, selectedMetrics]
  )

  /*
   * Section: Markup
   */
  const midlineEllipsisIcon =
    featureMultiMetrics &&
    metricsOptions?.length &&
    currentSectionItems.length > 1 &&
    // !isMetrics &&
    editorMode &&
    !isSharedView ? (
      <i
        className={classNames('icon-aytmfd-midline-hor-ellipsis', mcss['icon'], { 'highlighted-icon': isOptionsOpen })}
        onClick={toggleExtraMenu}
        {...(metricsOptions?.length ? {} : { 'data-disabled': true })}
      />
    ) : null

  const starIconId = useMemo(() => 'id' + String(Math.random()).slice(2), [])

  const starIcon =
    featureMultiMetrics && !isSharedView && editorMode && 'metrics' === type ? (
      <>
        <i
          id={starIconId}
          className={classNames('pi pi-star', mcss['icon'], mcss['icon-star'])}
          onClick={handleStarClick}
        />
        <Tooltip
          target={`#${starIconId}`}
          position="top"
          className={classNames('PRAYTMTheme', mcss['tooltip-star'])}
          autoHide={false}
        >
          Make default
        </Tooltip>
      </>
    ) : null

  const plusIcon =
    featureMultiMetrics && !isSharedView && editorMode && 'attribute' === type && metricsOptions?.length ? (
      <i
        className={classNames('pi icon-aytmfd-plus-medium', mcss['icon'], { 'highlighted-icon': isOptionsOpen })}
        {...(metricsOptions?.length ? {} : { 'data-disabled': true })}
        onClick={toggleOptions}
      />
    ) : null

  const squareDirectionIcon =
    featureMultiMetrics && 'attribute' === type && metricsOptions?.length && selectedMetrics.length ? (
      <i
        className={classNames('pi', showMetrics ? 'pi-chevron-down' : 'pi-chevron-right', mcss['icon'], {
          'highlighted-icon': Boolean(selectedMetrics.length)
        })}
        onClick={handleToggleMetrics}
      />
    ) : null

  const sortIcon = sorted ? (
    <i
      className={classNames(
        `${className}-icon-sort`,
        sorted && ('asc' === propsSortOrder ? `icon-aytmfd-arrow-up-smooth` : `icon-aytmfd-arrow-down-smooth`),
        {
          [`${className}-icon-sort-metric`]: 'metrics' === type
        }
      )}
    />
  ) : null

  const dragBarComponent =
    !isSharedView && 'attribute' === type ? (
      <DragBar
        className={`${className}-drag-bar`}
        attribute={attribute}
        section={section}
        target={target}
        onDrag={propsOnDrag}
      />
    ) : (
      <div className={`${className}-drag-bar-empty`} />
    )

  const attributeText = decode(attributes?.[attribute]?.text || attribute)
  const captionComponent = featureMultiMetrics ? (
    'metrics' === type ? (
      <div className={mcss['metrics-complex-label']}>
        <div className={mcss['metrics-title']}>
          {sortIcon}
          {prettyMetricsName(metrics?.name)}
        </div>
      </div>
    ) : defaultMetrics ? (
      <div className={mcss['metrics-complex-label']}>
        <div className={mcss['metrics-title']} title={attributeText}>
          {sortIcon}
          {attributeText}
        </div>
        <div className={mcss['metrics-sub-label']}>{prettyMetricsName(defaultMetrics.name)}</div>
      </div>
    ) : (
      <div className={mcss['metrics-title']} title={attributeText}>
        {sortIcon}
        {attributeText}
      </div>
    )
  ) : (
    attributeText
  )

  return (
    <div
      ref={node => void dragSource.current.connect(node)}
      className={classNames(className, {
        [mcss['attribute-metrics']]: Boolean('metrics' === type && metricsMethodId)
      })}
      {...(isSharedView ? { 'data-disabled': true } : {})}
    >
      <i className={`icon-aytmfd-delete ${className}-delete`} onClick={handleDeleteClick} />
      <div
        ref={thisDOMElRef}
        className={classNames(`${className}-name`, {
          [`${className}-name-drag`]: propsDrag,
          [`${className}-name-selected`]: selected && !propsDrag,
          [mcss['attribute-name-sorted']]: selected
        })}
        onClick={handleTextClick}
      >
        <div
          className={classNames(`${className}-text`, {
            [`${className}-text-selected`]: selected
          })}
        >
          {captionComponent}
        </div>
        <div className={`${className}-buttons`}>
          {midlineEllipsisIcon}
          {starIcon}
          {plusIcon}
          {squareDirectionIcon}
        </div>
      </div>
      {dragBarComponent}
      <OverlayPanel
        dismissable
        ref={panelRef}
        className={classNames(mcss['options-list-overlay'], 'insights-portal PRAYTMTheme')}
        prime-fixed-dropdown-dir="bottom"
        aytm-overlay-panel-patch="true"
        onHide={() => setIsOptionsOpen(false)}
      >
        {'menu' === showPanelContent && <ListBox value={null} options={menuOptions} onChange={handleAttributeMenu} />}
        {'metrics' === showPanelContent && (
          <AttributeOptionsList
            options={metricsOptions}
            selectedDefault={defaultMetrics?.id}
            selectedList={selectedMetrics}
            onSelect={handleSelectMetrics}
          />
        )}
      </OverlayPanel>
    </div>
  )
}

Attribute.displayName = 'Attribute'

const AttributePlaceholder = () => <div className={CLASS} />
AttributePlaceholder.displayName = 'AttributePlaceholder'

Attribute.Placeholder = AttributePlaceholder
